/**
 * HONEST MONEY (F3): the domain ALWAYS stores minor units (cents)
 * as integers — never floats. These functions are the only conversion
 * boundary to/from decimal text (UI inputs and K1 exports).
 */

/** Decimals per currency (ISO-4217 exponent). Default 2. */
const CURRENCY_DECIMALS: Record<string, number> = {
  CLP: 0,
  JPY: 0,
  KRW: 0,
  VND: 0,
  BHD: 3,
  KWD: 3,
  OMR: 3,
  TND: 3,
};

export function currencyDecimals(currency: string | null | undefined): number {
  return CURRENCY_DECIMALS[(currency ?? "").toUpperCase()] ?? 2;
}

/**
 * Minor units → honest decimal in text (`1234` → `"12.34"`), without going
 * through floats: the division is integer and the remainder is zero-padded.
 * Neutral format with decimal point (for exports/inputs); for a localized UI
 * use {@link formatMoney}.
 */
export function minorToDecimal(amountMinor: number, currency?: string | null): string {
  if (!Number.isInteger(amountMinor)) {
    throw new TypeError(`amountMinor must be an integer, got ${amountMinor}`);
  }
  const decimals = currencyDecimals(currency);
  if (decimals === 0) {
    return String(amountMinor);
  }
  const sign = amountMinor < 0 ? "-" : "";
  const abs = Math.abs(amountMinor);
  const base = 10 ** decimals;
  const units = Math.floor(abs / base);
  const rest = String(abs % base).padStart(decimals, "0");
  return `${sign}${units}.${rest}`;
}

/**
 * Decimal text → minor units. Accepts comma or decimal point (`"12,34"` and
 * `"12.34"`), spaces and sign. Returns `null` if the text is not a valid
 * amount or carries more decimals than the currency allows (never rounds
 * silently: money is not invented).
 */
export function parseDecimalToMinor(input: string, currency?: string | null): number | null {
  const decimals = currencyDecimals(currency);
  const trimmed = input.trim().replaceAll(" ", "");
  if (!trimmed) {
    return null;
  }
  const match = /^(-?)(\d+)(?:[,.](\d+))?$/.exec(trimmed);
  if (!match) {
    return null;
  }
  const [, sign, units, fraction = ""] = match;
  if (fraction.length > decimals) {
    return null;
  }
  const paddedFraction = fraction.padEnd(decimals, "0");
  const minor = Number(units) * 10 ** decimals + (decimals > 0 ? Number(paddedFraction) : 0);
  if (!Number.isSafeInteger(minor)) {
    return null;
  }
  return sign === "-" ? -minor : minor;
}

/** Localized amount for the UI (`1234` + `"EUR"` → `"12,34 €"` in es). */
export function formatMoney(
  amountMinor: number,
  currency: string | null | undefined,
  locale?: string,
): string {
  const code = (currency ?? "EUR").toUpperCase();
  const decimals = currencyDecimals(code);
  const value = amountMinor / 10 ** decimals;
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency: code }).format(value);
  } catch {
    // Currency unknown to Intl → honest decimal + code.
    return `${minorToDecimal(amountMinor, code)} ${code}`;
  }
}
